import type {
  BranchRunStatusesResponse,
  FailureNeighbors,
  RunExecutionStatus,
  StepExecutionItem,
  StepExecutionsResponse,
} from "../../shared/executions/executionListTypes";
import { useEffect } from "react";
import { queries } from "../../shared/queries";
import {
  failureNeighborsPath,
  runExecutionsPath,
  stepExecutionPath,
  stepExecutionsPath,
} from "../lib/apiPaths";
import { useQuery } from "./useQuery";
import { useRevisionedJson } from "./useRevisionedJson";
import {
  mergeStepExecutions,
  removeOptimisticStepExecutions,
  useOptimisticStepExecutions,
} from "./optimisticStepExecutions";

type ExecutionsState = {
  executions: StepExecutionItem[];
  loading: boolean;
};

// Executions from the retention-aware claims-plus-history read paths. Pass
// null params to disable.
function useExecutionResource(
  path: string,
  params: Record<string, string> | null,
  revision?: unknown
): ExecutionsState {
  const { data, loading } = useRevisionedJson<StepExecutionsResponse>(
    path,
    params ? new URLSearchParams(params).toString() : null,
    revision
  );
  return { executions: data?.executions ?? [], loading };
}

function useActivityRevision({
  branchId,
  stepDir,
}: {
  branchId?: string | null;
  stepDir?: string;
}) {
  const [rows] = useQuery(
    branchId ? queries.loadBranchStepActivity({ branchId }) : undefined
  );
  return [...(rows ?? [])]
    .filter((row) => !stepDir || row.stepDir === stepDir)
    .sort((a, b) => a.stepDir.localeCompare(b.stepDir))
    .map((row) => `${row.stepDir}:${row.updatedAt}`)
    .join("|");
}

export function useStepExecutions(
  workflowId: string,
  branchId: string,
  stepDir: string
): ExecutionsState {
  const revision = useActivityRevision({ branchId, stepDir });
  const retained = useExecutionResource(
    stepExecutionsPath(),
    workflowId && branchId && stepDir
      ? { workflowId, branchId, stepDir }
      : null,
    revision
  );
  const optimistic = useOptimisticStepExecutions(workflowId, branchId, stepDir);
  useEffect(() => {
    removeOptimisticStepExecutions(
      new Set(retained.executions.map((execution) => execution.id))
    );
  }, [retained.executions]);
  return {
    executions: mergeStepExecutions(retained.executions, optimistic),
    loading: retained.loading,
  };
}

const NO_NEIGHBOURS: FailureNeighbors = { older: null, newer: null };

// The nearest older/newer failure for the step, read from execution_failures
// so a jump reaches failures beyond the loaded execution window. Keyed by the
// pivot execution and refreshed on step activity.
export function useAdjacentFailures(
  workflowId: string,
  branchId: string,
  stepDir: string,
  pivot: { id: string; createdAt: number } | undefined
): FailureNeighbors {
  const revision = useActivityRevision({ branchId, stepDir });
  const enabled = Boolean(workflowId && branchId && stepDir && pivot);
  const { data } = useRevisionedJson<FailureNeighbors>(
    failureNeighborsPath(),
    enabled && pivot
      ? new URLSearchParams({
          workflowId,
          branchId,
          stepDir,
          executionId: pivot.id,
          createdAt: String(pivot.createdAt),
        }).toString()
      : null,
    revision
  );
  return data ?? NO_NEIGHBOURS;
}

export function useExecutionById(executionId: string | null | undefined): {
  execution: StepExecutionItem | undefined;
  loading: boolean;
} {
  const { data, loading } = useRevisionedJson<StepExecutionItem>(
    executionId ? stepExecutionPath(executionId) : "",
    executionId ? "" : null
  );
  return { execution: data ?? undefined, loading };
}

export function useStepTestExecutions(
  workflowId: string,
  branchId: string,
  stepDir?: string
): ExecutionsState {
  const revision = useActivityRevision({ branchId, stepDir });
  return useExecutionResource(
    stepExecutionsPath(),
    workflowId && branchId
      ? {
          workflowId,
          branchId,
          purpose: "step_test",
          limit: stepDir ? "50" : "200",
          ...(stepDir ? { stepDir } : {}),
        }
      : null,
    revision
  );
}

export function useInFlightStepTestCapture(
  workflowId: string,
  branchId: string,
  stepDir: string | null
): StepExecutionItem | undefined {
  const revision = useActivityRevision({
    branchId,
    stepDir: stepDir ?? undefined,
  });
  const { executions } = useExecutionResource(
    stepExecutionsPath(),
    workflowId && branchId && stepDir
      ? {
          workflowId,
          branchId,
          stepDir,
          purpose: "step_test_capture",
          status: "pending",
          limit: "1",
        }
      : null,
    revision
  );
  return executions[0];
}

export function useRunExecutions(
  runId: string | null | undefined
): ExecutionsState {
  const [run] = useQuery(runId ? queries.loadRunSummary({ runId }) : undefined);
  const revision = useActivityRevision({ branchId: run?.branchId });
  return useExecutionResource(
    runExecutionsPath(),
    runId && run ? { runId } : null,
    revision
  );
}

export function useBranchRunStatuses(branchId: string): {
  byRun: ReadonlyMap<string, RunExecutionStatus["status"]>;
  loading: boolean;
} {
  const revision = useActivityRevision({ branchId });
  const { data, loading } = useRevisionedJson<BranchRunStatusesResponse>(
    runExecutionsPath(),
    branchId ? new URLSearchParams({ branchId }).toString() : null,
    revision
  );
  const byRun = new Map(
    (data?.statuses ?? []).map((row) => [row.runId, row.status])
  );
  return { byRun, loading };
}
